import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tab,
  Tabs,
  Grid,
  IconButton,
  Tooltip,
  InputAdornment,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Handshake as HandshakeIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import partnershipStore from '../stores/partnershipStore';
import shopStore from '../stores/shopStore';
import logger from '../utils/logger';

const OptionsPage = observer(() => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [supplierTab, setSupplierTab] = useState(0); // Внутренние табы для поставщиков
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Состояние для торговой наценки
  const [markupPercentage, setMarkupPercentage] = useState('');
  const [markupAmount, setMarkupAmount] = useState('');
  const [markupSaved, setMarkupSaved] = useState(false);

  // Состояние для настроек магазина
  const [shopName, setShopName] = useState('');
  const [shopAddress, setShopAddress] = useState('');
  const [shopPhone, setShopPhone] = useState('');
  const [shopEmail, setShopEmail] = useState('');
  const [shopWebsite, setShopWebsite] = useState('');
  const [shopDataLoaded, setShopDataLoaded] = useState(false);
  const [shopSaved, setShopSaved] = useState(false);

  // Функция для проверки наличия торговой наценки
  const hasMarkupConfigured = () => {
    const markupPercentage = localStorage.getItem('markup_percentage');
    const markupAmount = localStorage.getItem('markup_amount');
    
    const hasPercentage = markupPercentage && !isNaN(parseFloat(markupPercentage)) && parseFloat(markupPercentage) > 0;
    const hasAmount = markupAmount && !isNaN(parseFloat(markupAmount)) && parseFloat(markupAmount) > 0;
    
    return hasPercentage || hasAmount;
  };

  useEffect(() => {
    // Загружаем данные при монтировании компонента
    partnershipStore.fetchOutgoingRequests();
    partnershipStore.fetchActivePartnerships();

    // Загружаем сохраненные значения наценки из localStorage
    const savedPercentage = localStorage.getItem('markup_percentage');
    const savedAmount = localStorage.getItem('markup_amount');

    if (savedPercentage) {
      setMarkupPercentage(savedPercentage);
    }
    if (savedAmount) {
      setMarkupAmount(savedAmount);
    }

    // Загружаем данные магазина
    loadShopData();
  }, []);

  // Функция загрузки данных магазина
  const loadShopData = async () => {
    try {
      await shopStore.checkShop();
      if (shopStore.shop) {
        setShopName(shopStore.shop.name || '');
        setShopAddress(shopStore.shop.address || '');
        
        // Парсим контактную информацию из JSON
        if (shopStore.shop.contact_info) {
          const contactInfo = shopStore.shop.contact_info;
          setShopPhone(contactInfo.тел || contactInfo.phone || '');
          setShopEmail(contactInfo.email || '');
          setShopWebsite(contactInfo.вебсайт || contactInfo.website || '');
        }
      }
      setShopDataLoaded(true);
    } catch (error) {
      logger.error('Ошибка при загрузке данных магазина:', error);
    }
  };

  // Функция сохранения данных магазина
  const saveShopData = async () => {
    try {
      const contactInfo = {};
      if (shopPhone.trim()) contactInfo.тел = shopPhone.trim();
      if (shopEmail.trim()) contactInfo.email = shopEmail.trim();
      if (shopWebsite.trim()) contactInfo.вебсайт = shopWebsite.trim();

      const shopData = {
        name: shopName.trim(),
        address: shopAddress.trim() || null,
        contact_info: Object.keys(contactInfo).length > 0 ? contactInfo : null
      };

      if (shopStore.shop) {
        // Обновляем существующий магазин
        await shopStore.updateShop(shopData);
      } else {
        // Создаем новый магазин
        await shopStore.createShop(shopData);
      }

      setShopSaved(true);
      setTimeout(() => setShopSaved(false), 2000);
      logger.log('Данные магазина сохранены');
    } catch (error) {
      logger.error('Ошибка при сохранении данных магазина:', error);
    }
  };

  // Функция сохранения наценки в localStorage
  const saveMarkupToLocalStorage = (percentage, amount) => {
    localStorage.setItem('markup_percentage', percentage || '');
    localStorage.setItem('markup_amount', amount || '');
    setMarkupSaved(true);
    setTimeout(() => setMarkupSaved(false), 2000); // Убираем уведомление через 2 секунды
    logger.log('Торговая наценка сохранена в localStorage');
  };

  // Обработчики изменения полей наценки
  const handleMarkupPercentageChange = (event) => {
    const value = event.target.value;
    // Проверяем, что введено число
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setMarkupPercentage(value);
      saveMarkupToLocalStorage(value, markupAmount);
    }
  };

  const handleMarkupAmountChange = (event) => {
    const value = event.target.value;
    // Проверяем, что введено число
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setMarkupAmount(value);
      saveMarkupToLocalStorage(markupPercentage, value);
    }
  };

  // Дебаунс для поиска
  const debouncedSearch = useCallback(
    (query) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const timeout = setTimeout(() => {
        if (query.length >= 2) {
          partnershipStore.searchManufacturers(query);
        } else {
          partnershipStore.clearSearch();
        }
      }, 500);

      setSearchTimeout(timeout);
    },
    [searchTimeout],
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleSendRequest = async (manufacturerId) => {
    try {
      await partnershipStore.sendPartnershipRequest(manufacturerId);
      logger.log('Запрос на партнерство отправлен');
    } catch (error) {
      logger.error('Ошибка при отправке запроса:', error);
    }
  };

  const handleRefresh = () => {
    partnershipStore.fetchOutgoingRequests();
    partnershipStore.fetchActivePartnerships();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSupplierTabChange = (event, newValue) => {
    setSupplierTab(newValue);
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'pending_approval':
        return <Chip icon={<PendingIcon />} label="Ожидает" color="warning" size="small" />;
      case 'approved':
        return <Chip icon={<CheckCircleIcon />} label="Одобрено" color="success" size="small" />;
      case 'rejected':
        return <Chip icon={<CancelIcon />} label="Отклонено" color="error" size="small" />;
      default:
        return null;
    }
  };

  const getRequestButtonProps = (manufacturerId) => {
    const status = partnershipStore.getPartnershipStatus(manufacturerId);

    switch (status) {
      case 'pending_approval':
        return { disabled: true, text: 'Запрос отправлен', color: 'warning' };
      case 'approved':
        return { disabled: true, text: 'Партнерство активно', color: 'success' };
      case 'rejected':
        return { disabled: false, text: 'Повторить запрос', color: 'primary' };
      default:
        return { disabled: false, text: 'Отправить запрос', color: 'primary' };
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Настройки магазина
          </Typography>
          <Tooltip title="Обновить данные">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab icon={<BusinessIcon />} label="Настройки магазина" />
            <Tab icon={<SettingsIcon />} label="Торговая наценка" />
            <Tab icon={<HandshakeIcon />} label="Поставщики" />
          </Tabs>
        </Box>

        {/* Настройки магазина */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Настройки магазина
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Укажите основную информацию о вашем магазине для партнеров и клиентов.
            </Typography>

            {shopSaved && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Настройки магазина сохранены
              </Alert>
            )}

            {shopStore.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {shopStore.error}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Название магазина *"
                  placeholder="Например: Стеклянный мир"
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  variant="outlined"
                  required
                  helperText="Официальное название вашего магазина"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Адрес магазина"
                  placeholder="Например: г. Москва, ул. Примерная, д. 123"
                  value={shopAddress}
                  onChange={(e) => setShopAddress(e.target.value)}
                  variant="outlined"
                  multiline
                  rows={2}
                  helperText="Полный адрес вашего магазина"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Телефон"
                  placeholder="+7 (xxx) xxx-xx-xx"
                  value={shopPhone}
                  onChange={(e) => setShopPhone(e.target.value)}
                  variant="outlined"
                  type="tel"
                  helperText="Контактный телефон магазина"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  placeholder="shop@example.com"
                  value={shopEmail}
                  onChange={(e) => setShopEmail(e.target.value)}
                  variant="outlined"
                  type="email"
                  helperText="Контактный email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Веб-сайт"
                  placeholder="www.example.com"
                  value={shopWebsite}
                  onChange={(e) => setShopWebsite(e.target.value)}
                  variant="outlined"
                  type="url"
                  helperText="Адрес веб-сайта магазина"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={saveShopData}
                disabled={shopStore.isLoading || !shopName.trim()}
                startIcon={
                  shopStore.isLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SaveIcon />
                  )
                }
                sx={{ minWidth: 120 }}
              >
                {shopStore.isLoading ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Торговая наценка */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Торговая наценка
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Настройте торговую наценку для расчета цен. Вы можете указать наценку в процентах или
              фиксированную сумму.
            </Typography>

            {/* Предупреждение если торговая наценка не настроена */}
            {!hasMarkupConfigured() && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  bgcolor: 'error.50',
                  border: 1,
                  borderColor: 'error.main',
                  '& .MuiAlert-icon': {
                    color: 'error.main'
                  }
                }}
                icon={<WarningIcon />}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  <strong>Внимание.</strong> Торговая наценка не выставлена. В расчете стоимости показывается закупочная цена. Выставите торговые наценки.
                </Typography>
              </Alert>
            )}

            {markupSaved && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Настройки торговой наценки сохранены
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Наценка в процентах"
                  placeholder="Например: 15"
                  value={markupPercentage}
                  onChange={handleMarkupPercentageChange}
                  type="number"
                  variant="outlined"
                  inputProps={{
                    min: 0,
                    step: 0.1,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <PercentIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Процентная наценка от себестоимости"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Фиксированная наценка"
                  placeholder="Например: 1000"
                  value={markupAmount}
                  onChange={handleMarkupAmountChange}
                  type="number"
                  variant="outlined"
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Фиксированная сумма наценки"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

           {/*  <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor:
                  theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.light
                      : theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                <InfoIcon fontSize="small" />
                Как работает наценка:
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <li>
                    Если указана процентная наценка: Цена = Себестоимость + (Себестоимость ×
                    Наценка%)
                  </li>
                  <li>
                    Если указана фиксированная наценка: Цена = Себестоимость + Фиксированная сумма
                  </li>
                  <li>
                    Если указаны оба вида: применяется процентная наценка + фиксированная сумма
                  </li>
                </Box>
              </Typography>
            </Box> */}
          </Paper>
        )}

        {/* Поставщики */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <HandshakeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Поставщики
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Управление партнерствами с производителями душевых кабин.
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={supplierTab} onChange={handleSupplierTabChange}>
                <Tab icon={<SearchIcon />} label="Поиск производителей" />
                <Tab
                  icon={<PendingIcon />}
                  label={`Мои запросы (${partnershipStore.outgoingRequests.length})`}
                />
                <Tab
                  icon={<HandshakeIcon />}
                  label={`Партнерства (${partnershipStore.activePartnerships.length})`}
                />
              </Tabs>
            </Box>

            {/* Поиск производителей */}
            {supplierTab === 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Найти производителей для сотрудничества
                </Typography>

            <TextField
              fullWidth
              label="Поиск по названию, адресу или контактам"
              placeholder="Введите название производителя..."
              value={searchQuery}
              onChange={handleSearchChange}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {partnershipStore.searchLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SearchIcon color="action" />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            {partnershipStore.searchError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {partnershipStore.searchError}
              </Alert>
            )}

            {searchQuery.length > 0 && searchQuery.length < 2 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Введите минимум 2 символа для поиска
              </Alert>
            )}

            {partnershipStore.searchResults.length > 0 && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {partnershipStore.searchResults.map((manufacturer) => {
                  const buttonProps = getRequestButtonProps(manufacturer.id);

                  return (
                    <Grid item xs={12} md={6} key={manufacturer.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {manufacturer.name}
                          </Typography>
                          {manufacturer.address && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationIcon fontSize="small" />
                              {manufacturer.address}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" />
                            {manufacturer.owner_email}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinkIcon fontSize="small" />
                            {manufacturer.slug}
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button
                            variant="contained"
                            color={buttonProps.color}
                            disabled={buttonProps.disabled || partnershipStore.submitting}
                            onClick={() => handleSendRequest(manufacturer.id)}
                            startIcon={
                              partnershipStore.submitting ? (
                                <CircularProgress size={16} />
                              ) : (
                                <HandshakeIcon />
                              )
                            }>
                            {buttonProps.text}
                          </Button>
                          {partnershipStore.getPartnershipStatus(manufacturer.id) && (
                            <Box ml={1}>
                              {getStatusChip(
                                partnershipStore.getPartnershipStatus(manufacturer.id),
                              )}
                            </Box>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {searchQuery.length >= 2 &&
              !partnershipStore.searchLoading &&
              partnershipStore.searchResults.length === 0 &&
              !partnershipStore.searchError && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Производители не найдены. Попробуйте изменить поисковый запрос.
                </Alert>
              )}
              </Box>
            )}

            {/* Мои запросы */}
            {supplierTab === 1 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  <PendingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Мои запросы на партнерство
                </Typography>

                {partnershipStore.outgoingError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {partnershipStore.outgoingError}
                  </Alert>
                )}

                {partnershipStore.outgoingLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : partnershipStore.outgoingRequests.length === 0 ? (
                  <Alert severity="info">У вас пока нет отправленных запросов на партнерство.</Alert>
                ) : (
                  <List>
                    {partnershipStore.outgoingRequests.map((request, index) => (
                      <React.Fragment key={request.id}>
                        <ListItem>
                          <ListItemText
                            primary={request.manufacturer_name}
                            secondary={
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <LocationIcon fontSize="small" />
                                  {request.manufacturer_address || 'Адрес не указан'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <EmailIcon fontSize="small" />
                                  {request.manufacturer_owner_email}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CalendarIcon fontSize="small" />
                                  Запрос отправлен:{' '}
                                  {new Date(request.requested_at).toLocaleDateString('ru-RU')}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            {getStatusChip(request.status)}
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < partnershipStore.outgoingRequests.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Активные партнерства */}
            {supplierTab === 2 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  <HandshakeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Активные партнерства
                </Typography>

                {partnershipStore.partnershipsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {partnershipStore.partnershipsError}
                  </Alert>
                )}

                {partnershipStore.partnershipsLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : partnershipStore.activePartnerships.length === 0 ? (
                  <Alert severity="info">
                    У вас пока нет активных партнерств. Найдите производителей и отправьте запросы на
                    сотрудничество.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {partnershipStore.activePartnerships.map((partnership) => (
                      <Grid item xs={12} md={6} key={partnership.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              {partnership.manufacturer_name}
                            </Typography>
                            {partnership.manufacturer_address && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationIcon fontSize="small" />
                                {partnership.manufacturer_address}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinkIcon fontSize="small" />
                              {partnership.manufacturer_slug}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon fontSize="small" />
                              Партнерство с:{' '}
                              {new Date(partnership.status_changed_at).toLocaleDateString('ru-RU')}
                            </Typography>
                            {partnership.shop_specific_discount_percentage !== 0 && (
                              <Typography
                                variant="body2"
                                color="primary"
                                fontWeight="bold"
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                {/* <PercentIcon fontSize="small" /> */}
                                {/* Скидка: {partnership.shop_specific_discount_percentage}% */}
                              </Typography>
                            )}
                          </CardContent>
                          <CardActions>{getStatusChip('approved')}</CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Paper>
        )}



        {partnershipStore.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {partnershipStore.error}
          </Alert>
        )}
      </Box>
    </Container>
  );
});

export default OptionsPage;
